import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useEventListener } from 'taboola-ultimate-ui';
import { authTokenManager } from '../../taboola-common-frontend-modules/authentication';
import { SESSION_STORAGE_KEYS } from '../consts/iframeParentConstants';

const TaApiContext = createContext();

export const TAApiProvider = ({ children, isMock = false }) => {
    const [isConnected, setIsConnected] = useState(false);
    const [accountId, setAccountId] = useState('');

    const handleMessage = useCallback(
        async event => {
            if (isConnected && !event.data.token && !event.data.taboolaToken) {
                return;
            }

            if ((!event.data.taboolaToken && !event.data.token) || !event.data.sessionExp) {
                return;
            }

            const tokenData = event.data;

            authTokenManager.setToken(tokenData.taboolaToken || tokenData.token, tokenData.sessionExp);
            setAccountId(tokenData.accountId);
            sessionStorage.setItem(SESSION_STORAGE_KEYS.ACCOUNT_ID, tokenData.accountId);
            sessionStorage.setItem(SESSION_STORAGE_KEYS.NUMERIC_ACCOUNT, tokenData.numericAccount);

            if (tokenData.validationResult) {
                setIsConnected(tokenData.validationResult.isValid);
            } else {
                setIsConnected(true);
            }
        },
        [isConnected]
    );

    useEventListener('message', handleMessage, window);

    useEffect(() => {
        if (isConnected) {
            return;
        }

        const hasToken = !!authTokenManager.getToken();
        const storedAccountId = sessionStorage.getItem(SESSION_STORAGE_KEYS.ACCOUNT_ID);

        if (hasToken && storedAccountId) {
            setAccountId(storedAccountId);
            setIsConnected(true);
        }
    }, [isConnected, setIsConnected, setAccountId]);

    const value = useMemo(
        () => ({
            isConnected,
            setIsConnected,
            accountId,
        }),
        [isConnected, setIsConnected, accountId]
    );

    if (!isConnected && !isMock) {
        return null;
    }

    return <TaApiContext.Provider value={value}>{children}</TaApiContext.Provider>;
};

export const useTAApi = () => {
    const context = useContext(TaApiContext);
    if (!context) {
        throw new Error('useTAApi must be used within a TAApiProvider');
    }
    return context;
};
